"use client";

import React, { useState } from "react";
import Link from "next/link";
import { UserOrNot } from "./user-or-not";
import { User as UserType } from "./user-or-not";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { UserAvatar } from "./user-avatar";
import { userMenu, headerMenu, MenuItem } from "./header-content";
import { ModeToggle } from "@/components/light-dark-toggle";
import { signOut } from "@/lib/auth-client";
import { useRouter } from "next/navigation";
import { Button } from "../ui/button";
import { DocsLink } from "./docs-link";
import { ContactFormDialog } from "@/components/contact-form-dialog";
import { useLoginLabel } from "@/hooks/use-login-label";

const renderMobileMenuItem = (
  item: MenuItem,
  nested = false
): React.ReactNode[] => {
  const isExternal = item.external ?? item.url.startsWith("http");
  const prefetch = item.prefetch ?? true;
  const itemClassName = nested
    ? "cursor-pointer min-h-9 pl-7 text-sm text-muted-foreground"
    : "cursor-pointer min-h-10";

  const link =
    item.title === "Docs" ? (
      <DocsLink key={item.url}>
        <CommandItem className={itemClassName}>
          {item.icon}
          {item.title}
        </CommandItem>
      </DocsLink>
    ) : isExternal ? (
      <a key={item.url} href={item.url} target="_blank" rel="noreferrer">
        <CommandItem className={itemClassName}>
          {item.icon}
          {item.title}
        </CommandItem>
      </a>
    ) : (
      <Link key={item.url} href={item.url} prefetch={prefetch}>
        <CommandItem className={itemClassName}>
          {item.icon}
          {item.title}
        </CommandItem>
      </Link>
    );

  if (!item.items) return [link];

  return [
    link,
    ...item.items.flatMap((child) => renderMobileMenuItem(child, true)),
  ];
};

function UserContent({ user }: { user: UserType }) {
  const router = useRouter();
  const doLogout = async () => {
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push("/");
        },
      },
    });
  };

  return (
    <Command>
      <CommandGroup>
        <CommandItem className="min-h-10">
          {user.email}
          <CommandShortcut>
            <UserAvatar user={user} className="size-6" />
          </CommandShortcut>
        </CommandItem>
        {userMenu.map((item) => (
          <Link key={item.url} href={item.url}>
            <CommandItem className="cursor-pointer min-h-10">
              {item.title}
            </CommandItem>
          </Link>
        ))}

        <CommandItem className="flex justify-between">
          Theme
          <ModeToggle />
        </CommandItem>
        <Link href="#" onClick={doLogout}>
          <CommandItem className="cursor-pointer min-h-10">Log Out</CommandItem>
        </Link>
      </CommandGroup>
      <CommandSeparator className="my-2" />
      <CommandGroup>
        {headerMenu.flatMap((item) => {
          // Flatten nested items for mobile
          if (item.items) {
            return item.items.flatMap((subItem) => renderMobileMenuItem(subItem));
          }

          const isExternal = item.external ?? item.url.startsWith("http");
          const prefetch = item.prefetch ?? true;

          // Special handling for Docs link to pass theme param
          if (item.title === "Docs") {
            return (
              <DocsLink key={item.url}>
                <CommandItem className="cursor-pointer min-h-10">
                  {item.title}
                </CommandItem>
              </DocsLink>
            );
          }

          return isExternal ? (
            <a
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noreferrer"
            >
              <CommandItem className="cursor-pointer min-h-10">
                {item.title}
              </CommandItem>
            </a>
          ) : (
            <Link key={item.url} href={item.url} prefetch={prefetch}>
              <CommandItem className="cursor-pointer min-h-10">
                {item.title}
              </CommandItem>
            </Link>
          );
        })}
      </CommandGroup>
    </Command>
  );
}

function NoUserContent() {
  const [contactOpen, setContactOpen] = useState(false);
  const loginLabel = useLoginLabel();

  return (
    <Command>
      <Button variant="outline" className="w-full mb-2" onClick={() => setContactOpen(true)}>
        Book a Call
      </Button>
      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
      <Button asChild className="w-full mb-2">
        <Link
          prefetch
          href={{ pathname: "/login", query: { redirectTo: "/me/api-keys" } }}
        >
          {loginLabel}
        </Link>
      </Button>
      <CommandSeparator className="my-2" />
      <CommandGroup>
        {headerMenu.flatMap((item) => {
          // Flatten nested items for mobile
          if (item.items) {
            return item.items.flatMap((subItem) => renderMobileMenuItem(subItem));
          }

          const isExternal = item.external ?? item.url.startsWith("http");
          const prefetch = item.prefetch ?? true;

          // Special handling for Docs link to pass theme param
          if (item.title === "Docs") {
            return (
              <DocsLink key={item.url}>
                <CommandItem className="cursor-pointer min-h-10">
                  {item.title}
                </CommandItem>
              </DocsLink>
            );
          }

          return isExternal ? (
            <a
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noreferrer"
            >
              <CommandItem className="cursor-pointer min-h-10">
                {item.title}
              </CommandItem>
            </a>
          ) : (
            <Link key={item.url} href={item.url} prefetch={prefetch}>
              <CommandItem className="cursor-pointer min-h-10">
                {item.title}
              </CommandItem>
            </Link>
          );
        })}
      </CommandGroup>
    </Command>
  );
}

export function LoginOrUserMobile() {
  return (
    <UserOrNot
      userContent={(user) => <UserContent user={user} />}
      noUserContent={<NoUserContent />}
    />
  );
}
